import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, ArrowLeft, Check, Loader2, Globe, Server, Mail } from "lucide-react";
import { useCart } from "@/lib/CartContext";

export default function CartDrawer() {
  const { state, plans, total, close, update, updateContact, submit } = useCart();
  return (
    <AnimatePresence>
      {state.open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[70]"
            onClick={close}
            data-testid="cart-overlay"
          />
          <motion.aside
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="fixed right-0 top-0 bottom-0 w-full sm:w-[560px] z-[80] bg-[color:var(--bg)] border-l border-[color:var(--border)] overflow-y-auto"
            data-testid="cart-drawer"
          >
            <div className="sticky top-0 z-10 flex items-center justify-between px-6 lg:px-8 h-16 border-b border-[color:var(--border)] bg-[color:var(--bg)]/90 backdrop-blur-xl">
              <div>
                <div className="overline">
                  {state.step === "config" && "Order · Configure"}
                  {state.step === "contact" && "Order · Contact"}
                  {state.step === "success" && "Order · Confirmed"}
                </div>
                <div className="font-serif text-lg tracking-tight">
                  {state.step === "success" ? state.order?.order_number : "Your basket"}
                </div>
              </div>
              <button onClick={close} className="w-9 h-9 grid place-items-center rounded-full border border-[color:var(--border)] hover:bg-[color:var(--surface-elev)]" aria-label="Close" data-testid="cart-close">
                <X size={16} />
              </button>
            </div>

            <div className="px-6 lg:px-8 py-8 pb-40">
              {state.step === "config" && <StepConfig state={state} plans={plans} update={update} total={total} />}
              {state.step === "contact" && <StepContact state={state} updateContact={updateContact} total={total} />}
              {state.step === "success" && <StepSuccess order={state.order} close={close} />}
            </div>

            {state.step !== "success" && (
              <div className="sticky bottom-0 border-t border-[color:var(--border)] bg-[color:var(--bg)]/95 backdrop-blur-xl p-5 lg:p-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="overline">Total</span>
                  <span className="font-serif text-3xl tracking-tight" data-testid="cart-total">£{total.toFixed(2)}</span>
                </div>
                {state.error && <div className="mb-3 text-[13px] text-red-500" data-testid="cart-error">{state.error}</div>}
                {state.step === "config" ? (
                  <button
                    onClick={() => update({ step: "contact" })}
                    disabled={total <= 0}
                    className="w-full h-12 rounded-full bg-[color:var(--text)] text-[color:var(--bg)] font-semibold text-[14px] inline-flex items-center justify-center gap-2 disabled:opacity-40 hover:opacity-90 transition-opacity"
                    data-testid="cart-continue"
                  >
                    Continue to contact <ArrowRight size={15} />
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <button
                      onClick={() => update({ step: "config", error: "" })}
                      className="h-12 px-5 rounded-full border border-[color:var(--border-strong)] font-medium text-[13px] inline-flex items-center gap-1.5"
                      data-testid="cart-back"
                    >
                      <ArrowLeft size={14} /> Back
                    </button>
                    <button
                      onClick={submit}
                      disabled={state.submitting || !canSubmit(state)}
                      className="flex-1 h-12 rounded-full bg-brand-primary text-white font-semibold text-[14px] inline-flex items-center justify-center gap-2 disabled:opacity-40"
                      data-testid="cart-submit"
                    >
                      {state.submitting ? <Loader2 size={16} className="animate-spin" /> : <>Reserve order <ArrowRight size={15} /></>}
                    </button>
                  </div>
                )}
                <p className="mt-3 text-[11.5px] text-[color:var(--text-muted)] leading-relaxed">
                  You'll receive an invoice by email. Registration &amp; provisioning starts after payment; our team confirms within 24 hours.
                </p>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}

function canSubmit(s) {
  return s.contact.name && s.contact.email && s.contact.phone;
}

function StepConfig({ state, plans, update, total }) {
  const noDomain = !state.domain;
  return (
    <div className="space-y-8">
      {/* Domain */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <Globe size={15} className="text-brand-primary" />
          <span className="overline">Domain</span>
        </div>
        {state.domain ? (
          <div className="p-4 rounded-2xl border border-[color:var(--border-strong)] flex items-center justify-between gap-4" data-testid="cart-domain-row">
            <div>
              <div className="font-mono text-[14.5px]">{state.domain.domain}</div>
              <div className="text-[11.5px] text-[color:var(--text-muted)] font-mono uppercase tracking-widest">
                {state.domain.status}{state.domain.price_gbp != null && ` · £${state.domain.price_gbp}/yr`}
              </div>
            </div>
            <button
              onClick={() => update({ domain: null, years: 1 })}
              className="text-[12px] text-[color:var(--text-muted)] hover:text-[color:var(--text)] underline"
              data-testid="cart-remove-domain"
            >
              Remove
            </button>
          </div>
        ) : (
          <p className="text-[13px] text-[color:var(--text-muted)]">No domain selected. Return to domain search to pick one — or continue with just hosting/email.</p>
        )}
        {state.domain && (
          <div className="mt-4">
            <div className="overline mb-2">Registration years</div>
            <div className="flex gap-2 flex-wrap">
              {[1, 2, 3, 5].map((y) => (
                <button
                  key={y}
                  onClick={() => update({ years: y })}
                  className={`h-10 px-4 rounded-lg border text-[13px] font-medium transition-colors ${
                    state.years === y ? "border-brand-primary bg-brand-primary/10" : "border-[color:var(--border-strong)] hover:bg-[color:var(--surface-elev)]"
                  }`}
                  data-testid={`cart-years-${y}`}
                >
                  {y} year{y > 1 ? "s" : ""}
                </button>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Hosting */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Server size={15} className="text-brand-primary" />
            <span className="overline">Web hosting</span>
          </div>
          <CycleToggle value={state.hostingCycle} onChange={(v) => update({ hostingCycle: v })} testId="hosting-cycle" />
        </div>
        <div className="grid grid-cols-1 gap-2">
          <PlanRow selected={!state.hostingPlanId} onSelect={() => update({ hostingPlanId: null })} title="No hosting" subtitle="I already have hosting" price={null} testId="hosting-none" />
          {plans.hosting.map((p) => (
            <PlanRow
              key={p.id}
              selected={state.hostingPlanId === p.id}
              onSelect={() => update({ hostingPlanId: p.id })}
              title={p.name}
              subtitle={p.tagline}
              price={state.hostingCycle === "month" ? p.price_month_gbp : p.price_year_gbp}
              cycle={state.hostingCycle}
              testId={`hosting-${p.id}`}
            />
          ))}
        </div>
      </section>

      {/* Email */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Mail size={15} className="text-brand-primary" />
            <span className="overline">Business email</span>
          </div>
          <CycleToggle value={state.emailCycle} onChange={(v) => update({ emailCycle: v })} testId="email-cycle" />
        </div>
        <div className="grid grid-cols-1 gap-2">
          <PlanRow selected={!state.emailPlanId} onSelect={() => update({ emailPlanId: null })} title="No email hosting" subtitle="Skip business email" price={null} testId="email-none" />
          {plans.email.map((p) => (
            <PlanRow
              key={p.id}
              selected={state.emailPlanId === p.id}
              onSelect={() => update({ emailPlanId: p.id })}
              title={p.name}
              subtitle={p.tagline}
              price={state.emailCycle === "month" ? p.price_month_gbp : p.price_year_gbp}
              cycle={state.emailCycle}
              testId={`email-${p.id}`}
            />
          ))}
        </div>
      </section>

      {noDomain && !state.hostingPlanId && !state.emailPlanId && (
        <div className="text-[12.5px] text-[color:var(--text-muted)] p-4 rounded-xl border border-dashed border-[color:var(--border)]">
          Pick a domain, hosting or email plan above to continue.
        </div>
      )}
    </div>
  );
}

function CycleToggle({ value, onChange, testId }) {
  return (
    <div className="inline-flex rounded-full border border-[color:var(--border)] p-0.5">
      {["month", "year"].map((v) => (
        <button
          key={v}
          onClick={() => onChange(v)}
          className={`px-3 h-7 rounded-full text-[11.5px] font-mono uppercase tracking-widest transition-colors ${
            value === v ? "bg-[color:var(--text)] text-[color:var(--bg)]" : "text-[color:var(--text-muted)]"
          }`}
          data-testid={`${testId}-${v}`}
        >
          {v}ly
        </button>
      ))}
    </div>
  );
}

function PlanRow({ selected, onSelect, title, subtitle, price, cycle, testId }) {
  return (
    <button
      onClick={onSelect}
      className={`w-full text-left p-4 rounded-xl border transition-colors flex items-center gap-3 ${
        selected ? "border-brand-primary bg-brand-primary/5" : "border-[color:var(--border-strong)] hover:bg-[color:var(--surface-elev)]"
      }`}
      data-testid={`cart-plan-${testId}`}
    >
      <span className={`w-4 h-4 rounded-full border-2 shrink-0 grid place-items-center ${selected ? "border-brand-primary" : "border-[color:var(--border-strong)]"}`}>
        {selected && <span className="w-2 h-2 rounded-full bg-brand-primary" />}
      </span>
      <div className="flex-1">
        <div className="font-medium text-[14px]">{title}</div>
        <div className="text-[12px] text-[color:var(--text-muted)]">{subtitle}</div>
      </div>
      {price != null && (
        <div className="text-right">
          <div className="font-mono text-[13.5px]">£{price}</div>
          <div className="text-[10.5px] text-[color:var(--text-muted)] font-mono uppercase tracking-widest">/{cycle}</div>
        </div>
      )}
    </button>
  );
}

function StepContact({ state, updateContact }) {
  return (
    <div className="space-y-6">
      <h3 className="font-serif text-3xl tracking-tight leading-tight">
        Just your <span className="italic">contact details</span>.
      </h3>
      <p className="text-[13.5px] text-[color:var(--text-muted)]">
        We'll email a secure payment link and start provisioning as soon as it clears.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Full name" value={state.contact.name} onChange={(v) => updateContact({ name: v })} testId="cart-name" required />
        <Field label="Email" type="email" value={state.contact.email} onChange={(v) => updateContact({ email: v })} testId="cart-email" required />
        <Field label="Phone" value={state.contact.phone} onChange={(v) => updateContact({ phone: v })} testId="cart-phone" required />
        <Field label="Company (optional)" value={state.contact.company} onChange={(v) => updateContact({ company: v })} testId="cart-company" />
      </div>
      <label className="block">
        <span className="overline block mb-2">Anything else? (optional)</span>
        <textarea
          rows={3}
          value={state.contact.notes}
          onChange={(e) => updateContact({ notes: e.target.value })}
          className="w-full rounded-xl border border-[color:var(--border-strong)] bg-transparent p-3 text-[13.5px] focus:border-brand-primary transition-colors resize-none"
          data-testid="cart-notes"
        />
      </label>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testId, required }) {
  return (
    <label className="block">
      <span className="overline block mb-2">{label}</span>
      <input
        type={type}
        value={value}
        required={required}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-11 rounded-xl border border-[color:var(--border-strong)] bg-transparent px-3.5 text-[14px] focus:border-brand-primary transition-colors"
        data-testid={testId}
      />
    </label>
  );
}

function StepSuccess({ order, close }) {
  if (!order) return null;
  return (
    <div className="text-center py-6" data-testid="cart-success">
      <div className="mx-auto w-14 h-14 rounded-full grid place-items-center bg-brand-primary text-white mb-5">
        <Check size={22} />
      </div>
      <h3 className="font-serif text-3xl tracking-tight leading-tight">
        Order <span className="italic">reserved</span>.
      </h3>
      <p className="mt-3 text-[14px] text-[color:var(--text-muted)] max-w-[400px] mx-auto">
        Reference <span className="font-mono">{order.order_number}</span>. A secure payment link is on its way to <span className="font-medium">{order.contact_email}</span>. We'll begin domain registration and provisioning as soon as it clears.
      </p>
      <div className="mt-8 text-left border border-[color:var(--border)] rounded-2xl p-5">
        <div className="overline mb-3">Order summary</div>
        <ul className="space-y-2 text-[13.5px]">
          {order.items.map((it, i) => (
            <li key={i} className="flex items-center justify-between">
              <span>{it.label}{it.qty > 1 && ` × ${it.qty}`}</span>
              <span className="font-mono">£{it.line_total_gbp.toFixed(2)}</span>
            </li>
          ))}
        </ul>
        <div className="mt-4 pt-4 border-t border-[color:var(--border)] flex items-center justify-between">
          <span className="overline">Total</span>
          <span className="font-serif text-2xl">£{order.total_gbp.toFixed(2)}</span>
        </div>
      </div>
      <button onClick={close} className="mt-8 h-11 px-6 rounded-full bg-[color:var(--text)] text-[color:var(--bg)] font-semibold text-[13px]" data-testid="cart-done">
        Done
      </button>
    </div>
  );
}
